import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Check, Search } from 'lucide-react';
import useOutsideClick from './useOutsideClick';

export interface MultiSelectOption {
    value: string;
    label: string;
}

interface MultiSelectChipsProps {
    options: MultiSelectOption[];
    selected: string[];
    onChange: (next: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
}

const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
    options, selected, onChange, placeholder = 'Select…', disabled, searchable = true,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // Stable handler + ignoreRefs so useOutsideClick doesn't re-attach its five
    // capture-phase document listeners on every render (that churn made opening
    // the dropdown feel laggy).
    const closeMenu = useCallback(() => setOpen(false), []);
    const ignoreRefs = useMemo(() => [panelRef], []);
    useOutsideClick(containerRef, closeMenu, ignoreRefs);

    useLayoutEffect(() => {
        if (!open || !containerRef.current) return;
        const update = () => {
            if (!containerRef.current) return;
            const r = containerRef.current.getBoundingClientRect();
            const GAP = 4;
            const MARGIN = 8; // keep a little breathing room above the viewport edge
            const spaceBelow = window.innerHeight - r.bottom - GAP - MARGIN;
            const spaceAbove = r.top - GAP - MARGIN;
            // Flip above the trigger when there's more room up top (e.g. trigger
            // near the bottom of the screen); otherwise open downward.
            const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
            const maxHeight = Math.max(140, openUp ? spaceAbove : spaceBelow);
            setPanelStyle(
                openUp
                    ? { position: 'fixed', bottom: window.innerHeight - r.top + GAP, left: r.left, width: r.width, maxHeight, zIndex: 10001 }
                    : { position: 'fixed', top: r.bottom + GAP, left: r.left, width: r.width, maxHeight, zIndex: 10001 }
            );
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    useEffect(() => { if (!open) setQuery(''); }, [open]);

    const labelByValue = useMemo(() => {
        const m: Record<string, string> = {};
        options.forEach(o => { m[o.value] = o.label; });
        return m;
    }, [options]);

    // Cap the number of rendered rows. Rendering hundreds/thousands of <li>
    // unvirtualized is what makes opening + each keystroke feel laggy on large
    // user lists. Show the first RENDER_CAP matches; searching narrows them.
    const RENDER_CAP = 50;
    const { filtered, hiddenCount } = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matches = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
        return { filtered: matches.slice(0, RENDER_CAP), hiddenCount: Math.max(0, matches.length - RENDER_CAP) };
    }, [options, query]);

    const toggle = (value: string) => {
        if (selected.includes(value)) onChange(selected.filter(v => v !== value));
        else onChange([...selected, value]);
    };

    const remove = (value: string) => onChange(selected.filter(v => v !== value));

    return (
        <div ref={containerRef} className={`fi-multi-select${open ? ' fi-multi-select--open' : ''}`}>
            <button
                type="button"
                data-testid="BTN_multiselect_trigger"
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                className="fi-multi-select__trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {selected.length === 0 ? (
                    <span className="fi-multi-select__placeholder">{placeholder}</span>
                ) : (
                    selected.map(v => (
                        <span key={v} className="fi-multi-select__chip">
                            {labelByValue[v] || v}
                            <button
                                type="button"
                                data-testid={`BTN_multiselect_chip_remove_${v}`}
                                onClick={(e) => { e.stopPropagation(); remove(v); }}
                                className="fi-multi-select__chip-remove"
                                aria-label={`Remove ${labelByValue[v] || v}`}
                            >
                                <X size={12} />
                            </button>
                        </span>
                    ))
                )}
                <span className="fi-multi-select__chevron" aria-hidden="true">
                    <ChevronDown size={14} />
                </span>
            </button>

            {open && !disabled && createPortal(
                <div ref={panelRef} className="fi-search-select__panel" role="listbox" style={panelStyle}>
                    {searchable && (
                        <div className="fi-multi-select__search-wrap">
                            <span className="fi-multi-select__search-icon" aria-hidden="true"><Search size={14} /></span>
                            <input
                                data-testid="INP_multiselect_search"
                                type="text"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Search…"
                                className="fi-multi-select__search-input"
                                style={{ outline: 'none', boxShadow: 'none' }}
                            />
                        </div>
                    )}
                    <ul className="fi-search-select__list">
                        {filtered.length === 0 ? (
                            <li className="fi-search-select__empty">No matches.</li>
                        ) : filtered.map(o => {
                            const checked = selected.includes(o.value);
                            return (
                                <li
                                    key={o.value}
                                    data-testid={`CHK_multiselect_option_${o.value}`}
                                    role="option"
                                    aria-selected={checked}
                                    className={`fi-search-select__option${checked ? ' fi-multi-select__option--checked' : ''}`}
                                    onMouseDown={e => { e.preventDefault(); toggle(o.value); }}
                                >
                                    <span className="fi-multi-select__checkbox" aria-hidden="true">
                                        <Check size={12} />
                                    </span>
                                    <span>{o.label}</span>
                                </li>
                            );
                        })}
                        {hiddenCount > 0 && (
                            <li className="fi-search-select__empty" style={{ fontStyle: 'italic' }}>
                                +{hiddenCount} more — type to refine…
                            </li>
                        )}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};

export default MultiSelectChips;
